import React from 'react';

type Variant = 'primary' | 'secondary' | 'purple' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  loading?: boolean;
  children: React.ReactNode;
}

const styles: Record<string, React.CSSProperties> = {
  base: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)',
    fontWeight: 700, borderRadius: 'var(--radius-md)',
    transition: 'all var(--transition-base)', letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
};

const variantStyles: Record<Variant, React.CSSProperties> = {
  primary:   { background: 'var(--accent)',   color: '#000', border: '1px solid var(--accent)' },
  secondary: { background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)' },
  purple:    { background: 'var(--accent2)',  color: '#fff', border: '1px solid var(--accent2)' },
  danger:    { background: 'var(--error-dim)', color: 'var(--error)', border: '1px solid rgba(239,68,68,0.3)' },
  ghost:     { background: 'transparent',     color: 'var(--text-dim)', border: '1px solid transparent' },
};

const sizeStyles: Record<Size, React.CSSProperties> = {
  sm: { padding: '6px 12px', fontSize: '0.75rem' },
  md: { padding: '9px 18px', fontSize: '0.82rem' },
  lg: { padding: '12px 24px', fontSize: '0.9rem' },
};

const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  children,
  disabled,
  style,
  ...rest
}) => {
  const isDisabled = disabled || loading;

  return (
    <button
      {...rest}
      disabled={isDisabled}
      style={{
        ...styles.base,
        ...variantStyles[variant],
        ...sizeStyles[size],
        opacity: isDisabled ? 0.45 : 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      {loading ? <span className="animate-spin">⟳</span> : icon}
      {children}
    </button>
  );
};

export default Button;
